import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query'
import { api } from '@/api/client'

export interface TransferAccount {
  id: string
  fb_user_id: string | null
  name: string | null
  profile_url: string | null
  status: 'active' | 'expired' | 'error'
  tasks_count: number
  paired_at: number | null
  last_used_at: number | null
  created_at: number | null
}

export interface TransferTask {
  id: string
  operation: string
  status: 'pending' | 'processing' | 'done' | 'failed'
  params: Record<string, unknown> | null
  result: Record<string, unknown> | null
  error_message: string | null
  account: {
    id: string
    name: string | null
    fb_user_id: string | null
    profile_url: string | null
  } | null
  started_at: number | null
  completed_at: number | null
  created_at: number | null
}

export interface TransferOperation {
  key: string
  label: string
  description: string
}

export interface AvailableProfile {
  id: string
  name: string
  fb_user_id: string | null
  is_online: boolean
  last_heartbeat_at: number | null
  pages: Array<{ id: string; name: string; fb_page_id: string | null }>
}

export function useAvailableProfiles() {
  return useQuery<AvailableProfile[]>({
    queryKey: ['transfer', 'available-profiles'],
    queryFn: () => api.get('/transfer/available-profiles'),
  })
}

export function useImportFromProfile() {
  const qc = useQueryClient()
  return useMutation({
    mutationFn: (data: { profile_id: string; name?: string }) =>
      api.post<TransferAccount & { updated: boolean }>('/transfer/import-profile', data),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ['transfer', 'accounts'] })
    },
  })
}

export function usePairFromCookies() {
  const qc = useQueryClient()
  return useMutation({
    mutationFn: (data: { cookies: string; name?: string; fb_dtsg?: string }) =>
      api.post<TransferAccount & { updated: boolean }>('/transfer/pair-cookies', data),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ['transfer', 'accounts'] })
    },
  })
}

export function useTransferAccounts() {
  return useQuery<TransferAccount[]>({
    queryKey: ['transfer', 'accounts'],
    queryFn: () => api.get('/transfer/accounts'),
  })
}

export function useTransferOperations() {
  return useQuery<TransferOperation[]>({
    queryKey: ['transfer', 'operations'],
    queryFn: () => api.get('/transfer/operations'),
  })
}

export function useTransferTasks(params?: { account_id?: string; status?: string; operation?: string }) {
  const searchParams = new URLSearchParams()
  if (params?.account_id) searchParams.set('account_id', params.account_id)
  if (params?.status) searchParams.set('status', params.status)
  if (params?.operation) searchParams.set('operation', params.operation)
  const qs = searchParams.toString()

  return useQuery<TransferTask[]>({
    queryKey: ['transfer', 'tasks', params],
    queryFn: () => api.get(`/transfer/tasks${qs ? `?${qs}` : ''}`),
  })
}

export function usePairAccount() {
  const qc = useQueryClient()
  return useMutation({
    mutationFn: (data: { curl: string; name?: string }) =>
      api.post<TransferAccount>('/transfer/accounts', data),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ['transfer', 'accounts'] })
    },
  })
}

export function useUpdateTransferAccount() {
  const qc = useQueryClient()
  return useMutation({
    mutationFn: ({ id, ...data }: { id: string; curl?: string; name?: string }) =>
      api.put<TransferAccount>(`/transfer/accounts/${id}`, data),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ['transfer', 'accounts'] })
    },
  })
}

export function useDeleteTransferAccount() {
  const qc = useQueryClient()
  return useMutation({
    mutationFn: (id: string) => api.delete<{ ok: boolean }>(`/transfer/accounts/${id}`),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ['transfer', 'accounts'] })
    },
  })
}

export function useCreateTransferTask() {
  const qc = useQueryClient()
  return useMutation({
    mutationFn: (data: { account_id: string; operation: string; params?: Record<string, unknown> }) =>
      api.post<TransferTask>('/transfer/tasks', data),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ['transfer', 'tasks'] })
      qc.invalidateQueries({ queryKey: ['transfer', 'accounts'] })
    },
  })
}

export function useDeleteTransferTask() {
  const qc = useQueryClient()
  return useMutation({
    mutationFn: (id: string) => api.delete<{ ok: boolean }>(`/transfer/tasks/${id}`),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ['transfer', 'tasks'] })
    },
  })
}

export function useClearTransferTasks() {
  const qc = useQueryClient()
  return useMutation({
    mutationFn: (status: 'done' | 'failed') =>
      api.delete<{ deleted: number }>(`/transfer/tasks/clear?status=${status}`),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ['transfer', 'tasks'] })
    },
  })
}

export function useReplayCurl() {
  return useMutation({
    mutationFn: (curl: string) =>
      api.post<{ success: boolean; status: number; data: unknown; raw: string | null }>('/transfer/replay', { curl }),
  })
}
